/**
 * SettingsScopeSections — the Settings that are not about the roster itself, grouped by WHO they apply to,
 * so it is clear whose a setting is before changing it:
 *
 *   Shared with this session — everyone working the session ID sees and edits the same thing;
 *   Only you, this session   — kept on this device, for this session ID only;
 *   Only you, every session  — kept on this device, whatever the session.
 *
 * The one place these are laid out; the Settings tab shows it with or without a roster.
 */
import type { ReactNode } from "react";
import { useSession } from "@/contexts/SessionContext";
import { DutyOptionsSection } from "@/components/DutyOptionsSection";
import { NoReliefAlertSection } from "@/components/NoReliefAlertSection";
import { AutoFollowCounterSection } from "@/components/AutoFollowCounterSection";
import { OtColorsSection } from "@/components/OtColorsSection";
import type { DutyUse } from "@/utils/dutyOptions";
import { NON_COUNTER_LABEL } from "@/utils/counterChoice";
import { SosPasteDefaultsSection } from "@/components/SosPasteDefaultsSection";

const SCOPES = {
  session: { label: "Shared with this session", tint: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  you_session: { label: "Only you · this session", tint: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  you_device: { label: "Only you · every session", tint: "#475569", bg: "#f8fafc", border: "#e2e8f0" },
} as const;

function Group({ scope, note, children }: { scope: keyof typeof SCOPES; note: ReactNode; children: ReactNode }) {
  const s = SCOPES[scope];
  return (
    <section
      data-testid={`settings-scope-${scope}`}
      style={{ background: "white", borderRadius: 8, marginTop: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflow: "hidden" }}
    >
      <header style={{ background: s.bg, borderBottom: `1px solid ${s.border}`, borderLeft: `4px solid ${s.tint}`, padding: "10px 16px" }}>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: s.tint }}>{s.label}</div>
        <div style={{ fontSize: 13, color: "#475569", marginTop: 2 }}>{note}</div>
      </header>
      <div style={{ padding: "4px 16px 16px", display: "flex", flexDirection: "column", gap: 18 }}>{children}</div>
    </section>
  );
}

function Item({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ paddingTop: 14 }}>
      <h4 style={{ margin: "0 0 8px", fontSize: 19, fontWeight: 700, letterSpacing: "-0.01em", color: "#0f172a" }}>{title}</h4>
      {children}
    </div>
  );
}

export function SettingsScopeSections({ dutyUse }: { dutyUse?: Record<string, DutyUse> }) {
  const { sessionId } = useSession();
  return (
    <>
      <Group scope="session" note={<>Everyone working session <b>{sessionId ?? "—"}</b> sees and can change these.</>}>
        <Item title={NON_COUNTER_LABEL}><DutyOptionsSection use={dutyUse} /></Item>
        <Item title="OT Colours"><OtColorsSection /></Item>
      </Group>

      <Group scope="you_session" note={<>Saved on this device, for session <b>{sessionId ?? "—"}</b> only. Other users and other sessions are not affected.</>}>
        <Item title="No Relief alert"><NoReliefAlertSection /></Item>
      </Group>

      <Group scope="you_device" note="Saved on this device. They apply to you in every session; other users keep their own.">
        <Item title="Add SOS"><SosPasteDefaultsSection /></Item>
        <Item title="Auto-follow counter"><AutoFollowCounterSection /></Item>
      </Group>
    </>
  );
}
